import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface BulkOperation {
  type: 'set_plan' | 'set_override' | 'assign_role';
  plan_code?: string;
  module_id?: string;
  enabled?: boolean;
  user_id?: string;
  role_code?: string;
}

interface BulkOperationParams {
  action: 'bulk_update';
  workspace_ids: string[];
  operations: BulkOperation[];
  dry_run?: boolean;
}

interface BulkOperationResult {
  success: boolean;
  successCount: number;
  errorCount: number;
  errors?: Array<{ workspace_id: string; error: string }>;
  dry_run: boolean;
}

export function useBulkOperations() {
  const queryClient = useQueryClient();

  const bulkMutation = useMutation({
    mutationFn: async (params: BulkOperationParams): Promise<BulkOperationResult> => {
      console.log('[useBulkOperations] Bulk operation:', params);
      
      const { data, error } = await supabase.functions.invoke('bulk-operations', {
        body: params
      });

      if (error) {
        console.error('[useBulkOperations] Error:', error);
        throw new Error(error.message || 'Failed to perform bulk operation');
      }

      if (!data) {
        throw new Error('No data received from bulk operation');
      }

      return data;
    },
    onSuccess: (data, variables) => {
      // Invalidate entitlements for all affected workspaces
      variables.workspace_ids.forEach(workspaceId => {
        queryClient.invalidateQueries({ queryKey: ['entitlements', 'workspace', workspaceId] });
        queryClient.invalidateQueries({ queryKey: ['workspace-modules', workspaceId] });
      });
      
      const { successCount, errorCount, dry_run } = data;
      
      if (dry_run) {
        toast.info(`Dry run completed: ${successCount} operations would succeed, ${errorCount} would fail`);
      } else if (errorCount === 0) {
        toast.success(`Bulk operation completed successfully for ${successCount} workspaces`);
      } else if (successCount > 0) {
        toast.warning(`Bulk operation completed with ${successCount} successes and ${errorCount} errors`);
      } else {
        toast.error(`Bulk operation failed for all ${errorCount} workspaces`);
      }
    },
    onError: (error: Error) => {
      console.error('[useBulkOperations] Error:', error);
      toast.error(error.message || 'Failed to perform bulk operation');
    },
    retry: 0
  });

  const applyPlanToWorkspaces = (workspaceIds: string[], planCode: string, dryRun = false) => {
    bulkMutation.mutate({
      action: 'bulk_update',
      workspace_ids: workspaceIds,
      operations: [{ type: 'set_plan', plan_code: planCode }],
      dry_run: dryRun
    });
  };

  const toggleModuleForWorkspaces = (workspaceIds: string[], moduleId: string, enabled: boolean, dryRun = false) => {
    bulkMutation.mutate({
      action: 'bulk_update',
      workspace_ids: workspaceIds,
      operations: [{ type: 'set_override', module_id: moduleId, enabled }],
      dry_run: dryRun
    });
  };

  const assignRoleToUsers = (workspaceIds: string[], userId: string, roleCode: string, dryRun = false) => {
    bulkMutation.mutate({
      action: 'bulk_update',
      workspace_ids: workspaceIds,
      operations: [{ type: 'assign_role', user_id: userId, role_code: roleCode }],
      dry_run: dryRun
    });
  };

  return {
    applyPlanToWorkspaces,
    toggleModuleForWorkspaces,
    assignRoleToUsers,
    bulkMutation: bulkMutation.mutate,
    isLoading: bulkMutation.isPending,
    error: bulkMutation.error,
    data: bulkMutation.data
  };
}